import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useMutation } from 'react-relay'
import { graphql } from 'relay-runtime'
import { IdToken, useAuth0 } from '@auth0/auth0-react'
import { Button } from '@mui/material'
import styled from '@emotion/styled'
import { cookies, CookieType } from '../../state/cookies'
import {
  LoginMutation,
  LoginMutation$data,
} from './__generated__/LoginMutation.graphql'
import { useAppDispatch } from '../../state/hooks'
import { Auth, setAuth, setViewingUserId } from '../../state/appSlice'
import { theme } from '../../mui'
import { AppDispatch } from '../../state/store'
import { trackLogin, resetIdentity } from '../../analytics/tracking'
import { identifyAndGroupUser } from '../../analytics/sessionIdentity'

const SignUpImage = require('../../assets/Sign Up Image.png')
const WordmarkBigBlue = require('../../assets/Wordmark_big_blue.png')

const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background-color: #ffffff;
`

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  box-sizing: border-box;
  min-width: 0;
`

const RightPanel = styled.div`
  flex-shrink: 0;
  height: 100%;
`

const SignUpImg = styled.img`
  height: 100%;
  width: auto;
  display: block;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 600px;
  width: 100%;
`

const Logo = styled.img`
  width: 200px;
  margin-bottom: 3rem;
`

const Heading = styled.h1`
  font-size: 48px;
  font-weight: 500;
  color: #003673;
  margin: 0 0 1rem 0;
  line-height: 1.2;
`

const Subheading = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #051E33;
  margin: 0 0 2rem 0;
`

const StyledButton = styled(Button)`
  background-color: #FF8A75 !important;
  color: #051E33 !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  padding: 0.875rem 3rem !important;
  border-radius: 4px !important;
  width: 180px;
  height: 48px;
  &:hover {
    background-color: #FF7A65 !important;
  }
  &:disabled {
    opacity: 0.6;
  }
`

const PrivacyPolicy = styled.div`
  color: #6B7280;
  font-size: 14px;
  margin-top: 2rem;
`

const PrivacyPolicyLink = styled.a`
  color: #6B7280;
  text-decoration: underline;
`

export const loginMutation = graphql`
  mutation LoginMutation($input: LoginRequest!) {
    login(input: $input) {
      id
      email
      firstName
      lastName
      token
      createdAt
      superuser
      accountRoles {
        role
      }
      account {
        id
        name
        createdAt
      }
    }
  }
`

export const loginSuccessCallback = (
  dispatch: AppDispatch,
  response: LoginMutation$data
) => {
  cookies.set(CookieType.AUTH.valueOf(), response.login)
  cookies.set(CookieType.VIEWING_USER.valueOf(), response.login.id)
  dispatch(setAuth({
    id: response.login.id,
    email: response.login.email ?? '',
    token: response.login.token ?? '',
    superuser: response.login.superuser ?? false,
    accountId: response.login.account.id,
  }))
  dispatch(setViewingUserId(response.login.id))

  resetIdentity()

  const ctx = identifyAndGroupUser(response.login)

  trackLogin(ctx)
}

export const loginErrorCallback = (error: Error) => {
  console.log(error)
  toast.error('Unable to log in.')
  cookies.remove(CookieType.AUTH)
  cookies.remove(CookieType.VIEWING_USER)
  if (window.location.pathname !== '/') {
    window.location = '/' as unknown as Location
  }
}

const Login = () => {
  const [idToken, setIdToken] = useState(undefined as IdToken | undefined)
  const { isLoading, loginWithPopup, getIdTokenClaims, user } = useAuth0()
  const [loginRequest, loginRequestLoading] =
    useMutation<LoginMutation>(loginMutation)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (user != null) {
      getIdTokenClaims().then((result) => setIdToken(result))
    }
  }, [user, getIdTokenClaims])

  useEffect(() => {
    if (idToken != null) {
      loginRequest({
        variables: {
          input: {
            accessToken: idToken.__raw!,
          },
        },
        onError: loginErrorCallback,
        onCompleted: (response) => loginSuccessCallback(dispatch, response),
      })
    }
  }, [idToken, dispatch, loginRequest])

  return (
    <PageContainer>
      <LeftSection>
        <Container>
          <Logo src={WordmarkBigBlue} alt="Purposely.ai" />
          <Heading>
            Understand and create what works with Purposely.ai
          </Heading>
          <Subheading>Log In with your work email below.</Subheading>
          <StyledButton
            disabled={isLoading || loginRequestLoading}
            onClick={() => loginWithPopup({ prompt: 'login' })}
            variant="contained"
          >
            LOG IN
          </StyledButton>
          <PrivacyPolicy>
            By logging in, you agree to Purposely's{' '}
            <PrivacyPolicyLink
              href="https://www.purposely.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              Privacy Policy
            </PrivacyPolicyLink>
            .
          </PrivacyPolicy>
        </Container>
      </LeftSection>
      <RightPanel>
        <SignUpImg src={SignUpImage} alt="Purposely testimonials" />
      </RightPanel>
    </PageContainer>
  )
}

export default Login